import {
  useEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type RefObject,
} from "react";
import type {
  CatalogueItem,
  Participant,
  ParticipantAnswer,
  Seat,
  SharedNote,
} from "../types";
import { MAX_PERSONAL_CONTEXT_LENGTH } from "../types";
import {
  answerIsComplete,
  createDraftAnswer,
  normalizeParticipantAnswer,
} from "../lib/session";
import { ResponseEditor } from "./ResponseControls";
import { BookmarkIcon } from "./Icons";
import { MobilePanelToggle } from "./MobilePanelToggle";

interface LocalItemWorkspaceProps {
  roomId: string;
  item: CatalogueItem;
  participants: Record<Seat, Participant>;
  answers: Record<Seat, ParticipantAnswer | null>;
  note: SharedNote | null;
  onSaveAnswer: (seat: Seat, answer: ParticipantAnswer) => Promise<void>;
  onSaveNote: (note: SharedNote) => Promise<void>;
  onAdvance: () => void;
  onRegisterBeforeNavigate: (
    handler: (() => Promise<void>) | null,
  ) => void;
  onDeleteCustom: ((item: CatalogueItem) => void) | null;
}

type FocusedField =
  | "A-context"
  | "note"
  | "B-context"
  | null;
type MobilePanel = "note";

export function LocalItemWorkspace({
  roomId,
  item,
  participants,
  answers,
  note,
  onSaveAnswer,
  onSaveNote,
  onAdvance,
  onRegisterBeforeNavigate,
  onDeleteCustom,
}: LocalItemWorkspaceProps) {
  const [drafts, setDrafts] = useState<Record<Seat, ParticipantAnswer>>(() =>
    initialDrafts(roomId, item, participants, answers),
  );
  const draftsRef = useRef(drafts);
  const [noteDraft, setNoteDraft] = useState(note?.text ?? "");
  const [noteBaseVersion, setNoteBaseVersion] = useState(note?.version ?? 0);
  const [rowImportant, setRowImportant] = useState(Boolean(note?.important));
  const [focused, setFocused] = useState<FocusedField>(null);
  const [contextOpen, setContextOpen] = useState<Record<Seat, boolean>>({
    A: Boolean(drafts.A.context),
    B: Boolean(drafts.B.context),
  });
  const [mobilePanels, setMobilePanels] = useState<Set<MobilePanel>>(
    () => new Set(),
  );
  const [message, setMessage] = useState("");
  const [saving, setSaving] = useState(false);
  const dirty = useRef<Record<Seat, boolean>>({ A: false, B: false });
  const noteDraftRef = useRef(noteDraft);
  const noteBaseVersionRef = useRef(noteBaseVersion);
  const rowImportantRef = useRef(rowImportant);
  const savedNoteRef = useRef({
    text: note?.text ?? "",
    important: Boolean(note?.important),
  });
  const answerSavedRef = useRef<Record<Seat, boolean>>({
    A: Boolean(answers.A?.savedAt),
    B: Boolean(answers.B?.savedAt),
  });
  const seatSaveRefs = useRef<Record<Seat, Promise<void> | null>>({
    A: null,
    B: null,
  });
  const pendingSaveRef = useRef<() => Promise<void>>(async () => undefined);
  const contextRefs = {
    A: useRef<HTMLTextAreaElement>(null),
    B: useRef<HTMLTextAreaElement>(null),
  };
  const responseRefs = {
    A: useRef<HTMLElement>(null),
    B: useRef<HTMLElement>(null),
  };
  const noteRef = useRef<HTMLTextAreaElement>(null);
  const mobileSaveRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const next = initialDrafts(roomId, item, participants, answers);
    draftsRef.current = next;
    setDrafts(next);
    setNoteDraft(note?.text ?? "");
    setNoteBaseVersion(note?.version ?? 0);
    setRowImportant(Boolean(note?.important));
    setContextOpen({
      A: Boolean(next.A.context),
      B: Boolean(next.B.context),
    });
    setMobilePanels(new Set());
    setFocused(null);
    setMessage("");
    dirty.current = { A: false, B: false };
    noteDraftRef.current = note?.text ?? "";
    noteBaseVersionRef.current = note?.version ?? 0;
    rowImportantRef.current = Boolean(note?.important);
    savedNoteRef.current = {
      text: note?.text ?? "",
      important: Boolean(note?.important),
    };
    answerSavedRef.current = {
      A: Boolean(answers.A?.savedAt),
      B: Boolean(answers.B?.savedAt),
    };
  }, [roomId, item.id]);

  useEffect(() => {
    setDrafts((current) => {
      let changed = false;
      const next = { ...current };
      for (const seat of ["A", "B"] as const) {
        const answer = answers[seat];
        if (!dirty.current[seat] && answer && answer !== current[seat]) {
          next[seat] = answer;
          answerSavedRef.current[seat] = Boolean(answer.savedAt);
          changed = true;
        }
      }
      if (changed) draftsRef.current = next;
      return changed ? next : current;
    });
  }, [answers.A?.version, answers.B?.version]);

  useEffect(() => {
    if (note && focused !== "note") {
      setNoteDraft(note.text);
      setNoteBaseVersion(note.version);
      setRowImportant(Boolean(note.important));
      noteDraftRef.current = note.text;
      noteBaseVersionRef.current = note.version;
      rowImportantRef.current = Boolean(note.important);
      savedNoteRef.current = {
        text: note.text,
        important: Boolean(note.important),
      };
    }
  }, [note?.version]);

  const updateDraft = (seat: Seat, answer: ParticipantAnswer) => {
    dirty.current[seat] = true;
    const next = { ...draftsRef.current, [seat]: answer };
    draftsRef.current = next;
    setDrafts(next);
    sessionStorage.setItem(draftKey(roomId, seat, item.id), JSON.stringify(answer));
  };

  const updateNoteDraft = (text: string) => {
    noteDraftRef.current = text;
    setNoteDraft(text);
  };

  const saveSharedNote = async (important = rowImportantRef.current) => {
    const text = noteDraftRef.current;
    if (
      text === savedNoteRef.current.text &&
      important === savedNoteRef.current.important
    ) {
      return;
    }
    const version = noteBaseVersionRef.current;
    await onSaveNote({
      itemId: item.id,
      text,
      important,
      updatedAt: Date.now(),
      version,
    });
    noteBaseVersionRef.current = version + 1;
    savedNoteRef.current = { text, important };
    setNoteBaseVersion(version + 1);
  };

  const saveSeat = async (seat: Seat, force = false): Promise<void> => {
    const answer = draftsRef.current[seat];
    if ((!dirty.current[seat] && !force) || !answerIsComplete(answer)) return;
    const pending = seatSaveRefs.current[seat];
    if (pending) {
      await pending;
      if (
        dirty.current[seat] ||
        (force && !answerSavedRef.current[seat])
      ) {
        await saveSeat(seat, force);
      }
      return;
    }
    const request = onSaveAnswer(seat, answer);
    seatSaveRefs.current[seat] = request;
    try {
      await request;
      answerSavedRef.current[seat] = true;
      const current = draftsRef.current[seat];
      const saved = {
        ...current,
        savedAt: Date.now(),
        version: Math.max(current.version, answer.version + 1),
      };
      const next = { ...draftsRef.current, [seat]: saved };
      draftsRef.current = next;
      setDrafts(next);
      if (current === answer) {
        dirty.current[seat] = false;
        sessionStorage.removeItem(draftKey(roomId, seat, item.id));
      }
    } finally {
      seatSaveRefs.current[seat] = null;
    }
  };

  const savePendingChanges = async () => {
    setSaving(true);
    try {
      await saveSharedNote();
      await saveSeat("A");
      await saveSeat("B");
    } catch (error) {
      setMessage(
        error instanceof Error
          ? error.message
          : "Changes could not be saved. Both drafts are preserved.",
      );
    } finally {
      setSaving(false);
    }
  };

  pendingSaveRef.current = savePendingChanges;

  useEffect(() => {
    const handler = () => pendingSaveRef.current();
    onRegisterBeforeNavigate(handler);
    return () => onRegisterBeforeNavigate(null);
  }, [onRegisterBeforeNavigate]);

  const completeItem = async () => {
    const current = draftsRef.current;
    const incompleteSeat = (["A", "B"] as const).find(
      (seat) => !answerIsComplete(current[seat]),
    );
    if (incompleteSeat) {
      setMessage(
        `Choose interest and experience for ${participants[incompleteSeat].name}.`,
      );
      focusFirstScale(responseRefs[incompleteSeat].current);
      return;
    }
    setSaving(true);
    setMessage("");
    try {
      await saveSharedNote();
      await saveSeat("A", !answerSavedRef.current.A);
      await saveSeat("B", !answerSavedRef.current.B);
      for (const seat of ["A", "B"] as const) {
        sessionStorage.removeItem(draftKey(roomId, seat, item.id));
      }
      dirty.current = { A: false, B: false };
      setMessage("Saved for both of you.");
      onAdvance();
    } catch (error) {
      setMessage(
        error instanceof Error
          ? error.message
          : "This item could not be saved. Both drafts are preserved.",
      );
    } finally {
      setSaving(false);
    }
  };

  const focusField = (
    field: Exclude<FocusedField, null>,
    ref: RefObject<HTMLElement | null>,
  ) => {
    setFocused(field);
    requestAnimationFrame(() => focusForFlow(ref.current));
  };

  const isMobileFlow = () =>
    window.matchMedia("(max-width: 760px)").matches;

  const finishFirstResponse = () => {
    void saveSeat("A");
    if (isMobileFlow()) {
      setFocused(null);
      requestAnimationFrame(() => focusFirstScale(responseRefs.B.current));
      return;
    }
    focusField("A-context", contextRefs.A);
  };

  const finishSecondResponse = () => {
    if (isMobileFlow()) {
      setFocused(null);
      requestAnimationFrame(() => focusForFlow(mobileSaveRef.current));
      return;
    }
    focusField("B-context", contextRefs.B);
  };

  const toggleMobilePanel = (
    panel: MobilePanel,
    focusTarget: HTMLElement | null = null,
  ) => {
    const opening = !mobilePanels.has(panel);
    setMobilePanels((current) => {
      const next = new Set(current);
      if (next.has(panel)) next.delete(panel);
      else next.add(panel);
      return next;
    });
    if (opening && focusTarget) {
      requestAnimationFrame(() => focusForFlow(focusTarget));
    }
  };

  const handleTextShortcut = (
    event: KeyboardEvent<HTMLTextAreaElement>,
    field: Exclude<FocusedField, null>,
  ) => {
    if (event.key !== "Enter" || !event.shiftKey) return;
    event.preventDefault();
    if (field === "A-context") {
      focusField("note", noteRef);
    } else if (field === "note") {
      setFocused(null);
      focusFirstScale(responseRefs.B.current);
    } else if (field === "B-context") {
      setFocused(null);
      void completeItem();
    }
  };

  const noteChanged =
    noteDraft !== savedNoteRef.current.text ||
    rowImportant !== savedNoteRef.current.important;

  const toggleRowImportant = async () => {
    const next = !rowImportantRef.current;
    rowImportantRef.current = next;
    setRowImportant(next);
    setSaving(true);
    try {
      await saveSharedNote(next);
    } catch (error) {
      rowImportantRef.current = !next;
      setRowImportant(!next);
      setMessage(
        error instanceof Error
          ? error.message
          : "The bookmark could not be saved.",
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <article
      className={`item-workspace local-workspace glass-panel ${focused ? "text-focused" : ""} ${rowImportant ? "row-important" : ""}`}
      aria-labelledby="current-item-title"
    >
      <button
        className="row-bookmark"
        type="button"
        aria-label={rowImportant ? "Remove row bookmark" : "Bookmark this row"}
        aria-pressed={rowImportant}
        onClick={() => void toggleRowImportant()}
      >
        <BookmarkIcon />
      </button>
      <section className="item-reference">
        {item.custom ? <span className="custom-label">Custom term</span> : null}
        <h1 id="current-item-title">{item.term}</h1>
        <p>{item.definition}</p>
        <p className="save-status" role="status" aria-live="polite">
          {message || (noteChanged ? "Shared note has unsaved changes." : "")}
        </p>
        <button
          className="button primary save-button"
          type="button"
          onClick={() => void completeItem()}
          disabled={saving}
        >
          {saving ? "Saving…" : "Save"}
        </button>
        {item.custom && !item.locked && onDeleteCustom ? (
          <button
            className="text-button danger"
            type="button"
            onClick={() => onDeleteCustom(item)}
          >
            Delete custom term
          </button>
        ) : null}
      </section>

      <LocalResponseColumn
        seat="A"
        participant={participants.A}
        answer={drafts.A}
        autoFocus={!answerIsComplete(drafts.A)}
        responseRef={responseRefs.A}
        contextRef={contextRefs.A}
        contextOpen={contextOpen.A}
        focused={focused}
        onContextOpen={() => {
          if (contextOpen.A) {
            setContextOpen((current) => ({ ...current, A: false }));
            setFocused(null);
            return;
          }
          setContextOpen((current) => ({ ...current, A: true }));
          focusField("A-context", contextRefs.A);
        }}
        onContextFocus={() => setFocused("A-context")}
        onContextBlur={() => setFocused(null)}
        onTextShortcut={handleTextShortcut}
        onChange={(answer) => updateDraft("A", answer)}
        onComplete={finishFirstResponse}
        onNavigateHorizontal={(direction) => {
          if (direction > 0) {
            finishFirstResponse();
          }
        }}
      />

      <section
        className={`shared-note-column mobile-collapsible ${
          mobilePanels.has("note") ? "mobile-expanded" : ""
        }`}
      >
        <MobilePanelToggle
          label="Shared notes"
          expanded={mobilePanels.has("note")}
          controls="local-shared-note-panel"
          onToggle={() => toggleMobilePanel("note", noteRef.current)}
        />
        <label
          id="local-shared-note-panel"
          className="mobile-panel-content"
        >
          <span className="column-label">Shared notes</span>
          <textarea
            ref={noteRef}
            data-flow-control
            aria-label="Shared notes"
            value={noteDraft}
            maxLength={2_000}
            placeholder="Questions or words you agree on"
            onFocus={() => {
              if (focused !== "note") setNoteBaseVersion(note?.version ?? 0);
              setFocused("note");
            }}
            onBlur={() => setFocused(null)}
            onKeyDown={(event) => handleTextShortcut(event, "note")}
            onChange={(event) => updateNoteDraft(event.target.value)}
          />
          {focused === "note" ? (
            <LocalTextShortcut action="partner" />
          ) : null}
        </label>
      </section>

      <LocalResponseColumn
        seat="B"
        participant={participants.B}
        answer={drafts.B}
        autoFocus={answerIsComplete(drafts.A) && !answerIsComplete(drafts.B)}
        responseRef={responseRefs.B}
        contextRef={contextRefs.B}
        contextOpen={contextOpen.B}
        focused={focused}
        onContextOpen={() => {
          if (contextOpen.B) {
            setContextOpen((current) => ({ ...current, B: false }));
            setFocused(null);
            return;
          }
          setContextOpen((current) => ({ ...current, B: true }));
          focusField("B-context", contextRefs.B);
        }}
        onContextFocus={() => setFocused("B-context")}
        onContextBlur={() => setFocused(null)}
        onTextShortcut={handleTextShortcut}
        onChange={(answer) => updateDraft("B", answer)}
        onComplete={finishSecondResponse}
        onNavigateHorizontal={(direction) => {
          if (direction < 0) {
            void saveSeat("B");
            focusField("note", noteRef);
          }
        }}
      />

      <button
        className="button primary mobile-save-button"
        type="button"
        ref={mobileSaveRef}
        onClick={() => void completeItem()}
        disabled={saving}
      >
        {saving ? "Saving…" : "Next"}
      </button>

    </article>
  );
}

function LocalResponseColumn({
  seat,
  participant,
  answer,
  autoFocus,
  responseRef,
  contextRef,
  contextOpen,
  focused,
  onContextOpen,
  onContextFocus,
  onContextBlur,
  onTextShortcut,
  onChange,
  onComplete,
  onNavigateHorizontal,
}: {
  seat: Seat;
  participant: Participant;
  answer: ParticipantAnswer;
  autoFocus: boolean;
  responseRef: RefObject<HTMLElement | null>;
  contextRef: RefObject<HTMLTextAreaElement | null>;
  contextOpen: boolean;
  focused: FocusedField;
  onContextOpen: () => void;
  onContextFocus: () => void;
  onContextBlur: () => void;
  onTextShortcut: (
    event: KeyboardEvent<HTMLTextAreaElement>,
    field: Exclude<FocusedField, null>,
  ) => void;
  onChange: (answer: ParticipantAnswer) => void;
  onComplete: () => void;
  onNavigateHorizontal: (direction: -1 | 1) => void;
}) {
  const contextField = `${seat}-context` as const;
  const contextPointerScrollRef = useRef<number | null>(null);
  return (
    <section
      className={`own-response local-response seat-${seat.toLocaleLowerCase()}-response`}
      ref={responseRef}
      aria-label={`${participant.name}'s response`}
    >
      <ResponseEditor
        answer={answer}
        autoFocus={autoFocus}
        onChange={onChange}
        onComplete={onComplete}
        onNavigateHorizontal={onNavigateHorizontal}
      />
      <div className="context-controls">
        <button
          className="text-button accent"
          type="button"
          aria-expanded={contextOpen}
          onClick={onContextOpen}
        >
          Context
        </button>
      </div>
      <div
        className={`context-editor ${contextOpen ? "" : "context-collapsed"}`}
      >
          <span className="column-label context-label">Context</span>
          <textarea
            ref={contextRef}
            data-flow-control
            aria-label={`${participant.name}’s personal context`}
            value={answer.context}
            placeholder="When or how does this work for you, or how do you define this?"
            maxLength={MAX_PERSONAL_CONTEXT_LENGTH}
            onFocus={() => {
              onContextFocus();
            }}
            onBlur={onContextBlur}
            onKeyDown={(event) => onTextShortcut(event, contextField)}
            onChange={(event) =>
              onChange({ ...answer, context: event.target.value })
            }
          />
          <button
            className="important-toggle context-important-toggle"
            type="button"
            role="checkbox"
            aria-checked={answer.important}
            onPointerDown={() => {
              contextPointerScrollRef.current =
                responseRef.current?.closest<HTMLElement>(
                  ".catalogue-stream",
                )?.scrollTop ?? null;
            }}
            onClick={() => {
              const stream = responseRef.current?.closest<HTMLElement>(
                ".catalogue-stream",
              );
              onChange({ ...answer, important: !answer.important });
              requestAnimationFrame(() => {
                const preservedScroll = contextPointerScrollRef.current;
                if (stream && preservedScroll !== null) {
                  stream.scrollTop = preservedScroll;
                } else if (stream) {
                  centreActiveRow(stream);
                }
                contextPointerScrollRef.current = null;
                contextRef.current?.focus({ preventScroll: true });
                if (stream && preservedScroll !== null) {
                  requestAnimationFrame(() => {
                    stream.scrollTop = preservedScroll;
                  });
                }
              });
            }}
          >
            <span className="checkbox-mark" aria-hidden="true" />
            <span>This context is important</span>
          </button>
          {focused === contextField ? (
            <LocalTextShortcut action="next" />
          ) : null}
      </div>
    </section>
  );
}

function LocalTextShortcut({
  action,
}: {
  action: "next" | "partner";
}) {
  const instruction =
    action === "partner"
      ? "Shift + Enter for your partner"
      : "Shift + Enter for the next column";
  return (
    <small className="text-shortcut">
      {instruction} · Enter for a new line
    </small>
  );
}

function initialDrafts(
  roomId: string,
  item: CatalogueItem,
  participants: Record<Seat, Participant>,
  answers: Record<Seat, ParticipantAnswer | null>,
) {
  return {
    A: readDraft(
      draftKey(roomId, "A", item.id),
      createDraftAnswer(item, participants.A.stance, answers.A ?? undefined),
      item,
      participants.A.stance,
    ),
    B: readDraft(
      draftKey(roomId, "B", item.id),
      createDraftAnswer(item, participants.B.stance, answers.B ?? undefined),
      item,
      participants.B.stance,
    ),
  };
}

function draftKey(roomId: string, seat: Seat, itemId: string) {
  return `feel-scene:draft:${roomId}:${seat}:${itemId}`;
}

function readDraft(
  key: string,
  fallback: ParticipantAnswer,
  item: CatalogueItem,
  stance: Participant["stance"],
) {
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw) as ParticipantAnswer;
    return parsed.itemId === fallback.itemId
      ? normalizeParticipantAnswer(parsed, item, stance)
      : fallback;
  } catch {
    return fallback;
  }
}

function focusFirstScale(owner: HTMLElement | null) {
  const scale = owner?.querySelector<HTMLElement>(".choice-scale");
  focusForFlow(scale ?? null);
}

function focusForFlow(element: HTMLElement | null) {
  if (!element) return;
  element.focus({ preventScroll: true });
  if (window.innerWidth <= 760) {
    element.scrollIntoView({
      block: "center",
      behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches
        ? "auto"
        : "smooth",
    });
  }
}

function centreActiveRow(container: HTMLElement) {
  if (!window.matchMedia("(min-width: 1101px)").matches) return;
  const active = container.querySelector<HTMLElement>("[data-active-item]");
  if (!active) return;
  const containerBounds = container.getBoundingClientRect();
  const activeBounds = active.getBoundingClientRect();
  const header = active
    .closest(".catalogue-category")
    ?.querySelector<HTMLElement>(".catalogue-category-toggle");
  const headerHeight = header?.offsetHeight ?? 0;
  const availableHeight = container.clientHeight - headerHeight;
  if (activeBounds.height > availableHeight) return;
  const desiredTop =
    headerHeight + (availableHeight - activeBounds.height) / 2;
  container.scrollTop = Math.max(
    0,
    container.scrollTop +
      activeBounds.top -
      containerBounds.top -
      desiredTop,
  );
}
